import type {
  Follow,
  LoadedRange,
  PaginatedGetItem,
  PaginatedLoader,
  PaginatedLoadingContextValue,
  StopLoader,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import {
  createContext,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"
import type {
  FC,
  ReactNode,
} from "react"
import type { RxCollection } from "rxdb"
import { useRxCollection } from "rxdb-hooks"
import {
  defaultState,
  AggregateLoadingContextUpdater,
} from "./AggregateLoadingContextUpdater"
import type { UseAggregateReloadingContext } from "./createAggregateReloadingContextProvider"
import { createAggregateReloadingContextProvider } from "./createAggregateReloadingContextProvider"
import { createLocalStorageGetterSetter } from "./createLocalStorageGetterSetter"
import { createPaginatedLoadingContextProvider } from "./createPaginatedLoadingContextProvider"
import { useFollowedUsers } from "src/contexts/FollowedUsersContext"
import { usePageLifecycleContext } from "src/contexts/PageLifecycleContext"
import { useFollowingFollows } from "src/hooks/follows/useFollowingFollows"
import { usePromise } from "src/hooks/utils/usePromise"
import type {
  CollectionKey,
  LocalStorageKey,
} from "src/types"
import { PageLifecycle } from "src/types"

type SetAccountIdState = (
  accountId: Follow["following_account_id"],
  state: PaginatedLoadingContextValue
) => void

type State = Record<
  Follow["following_account_id"],
  PaginatedLoadingContextValue | undefined
>

type UseAggregateLoadingContext = (
  accountId?: Follow["following_account_id"]
) => PaginatedLoadingContextValue

type LoadedRangesById = Record<Follow["id"], LoadedRange[]>

function serializeLoadedRanges(value: LoadedRangesById): string {
  return JSON.stringify(value)
}

function deserializeLoadedRanges(
  storedValue: string,
  defaultValue: LoadedRangesById,
): LoadedRangesById {
  try {
    const loadedRangesById = JSON.parse(storedValue) as LoadedRangesById
    return loadedRangesById
  } catch {
    return defaultValue
  }
}

interface Props {
  children?: ReactNode;
}

/*
 * this creates a loading context provider for every follow and stores the
 * state of each loading context indexed by the account id of the follow
 *
 * note: this is currently only set up for PaginatedLoadingContexts,
 * which is events and entries
 */
export function createAggregateLoadingContextProvider<T>(
  localStorageKey: LocalStorageKey,
  collectionKey: CollectionKey,
  useLoader: () => [
    PaginatedLoadingContextValue,
    PaginatedLoader<T>,
    StopLoader,
  ],
  useGetItems: () => undefined | PaginatedGetItem<T>,
): [FC<Props>, UseAggregateLoadingContext, UseAggregateReloadingContext] {
  const [
    Provider,
    useContextHook,
  ] = createPaginatedLoadingContextProvider(
    collectionKey,
    useLoader,
  )

  const [
    AggregateReloadingContextProvider,
    useAggregatReloadingContext,
  ] =
    createAggregateReloadingContextProvider()

  const AggregateLoadingContext = createContext<State>({})

  const useAggregateLoadingContext = (
    accountId?: Follow["following_account_id"],
  ): PaginatedLoadingContextValue => {
    const context = useContext(AggregateLoadingContext)
    const { currentUserId } = useFollowedUsers()
    return context[accountId ?? currentUserId ?? -1] ?? defaultState
  }

  const [
    getAggregatedLoadedRanges,
    setAggregatedLoadedRanges,
  ] =
    createLocalStorageGetterSetter({
      key: localStorageKey,
      serializeValue: serializeLoadedRanges,
      deserializeValue: deserializeLoadedRanges,
    })

  const AggregateLoadingContextProvider = (props: Props) => {
    const followingFollows = useFollowingFollows()
    const [
      state,
      setState,
    ] = useState<State>({})
    const stateRef = useRef<State>(state)
    const getItems = useGetItems()
    const pageLifecycle = usePageLifecycleContext()
    const rxCollection = useRxCollection(collectionKey) as RxCollection<T>

    const setFollowIdState = useCallback<SetAccountIdState>(
      (accountId, loadingState) => {
        stateRef.current = {
          ...stateRef.current,
          [accountId]: loadingState,
        }

        setState(stateRef.current)
      },
      [
        setState,
        stateRef,
      ],
    )

    // the loadedRangesPromise will resolve when loaded from AsyncStorage
    const [
      loadedRangesPromise,
      resolveLoadedRanges,
    ] =
      usePromise<LoadedRangesById>()

    // the valueRef will hold the currrent value of the loaded ranges
    const valueRef = useRef<LoadedRangesById>({})

    // get the loaded ranges from AsyncStorage
    useEffect(
      () => {
        void (async () => {
          const value = await getAggregatedLoadedRanges({})
          valueRef.current = value
          resolveLoadedRanges(value)
        })()
      },
      [
        valueRef,
        resolveLoadedRanges,
      ],
    )

    const getLoadedRangesForAccountId = useCallback(
      async (
        accountId: Follow["following_account_id"],
        defaultValue: LoadedRange[],
      ) => {
        await loadedRangesPromise
        const value = valueRef.current
        return value[accountId] ?? defaultValue
      },
      [
        loadedRangesPromise,
        valueRef,
      ],
    )

    const setLoadedRangesForAccountId = useCallback(
      (
        accountId: Follow["following_account_id"],
        loadedRanges: LoadedRange[],
      ) => {
        valueRef.current = {
          ...valueRef.current,
          [accountId]: loadedRanges,
        }

        return setAggregatedLoadedRanges(valueRef.current)
      },
      [valueRef],
    )

    // request recent readings each time the app is foregrounded
    useEffect(
      () => {
        if (
          !getItems ||
        (pageLifecycle !== PageLifecycle.Active && pageLifecycle !== PageLifecycle.Passive)
        ) {
          return
        }


        const call = async () => {
          try {
            const [items] = await getItems({
              params: {
                after: dayjs().subtract(
                  15,
                  "minutes",
                )
                  .toISOString(),
                all_follows: true,
              },
            })

            if (!items.length) {
              return
            }

            await rxCollection?.bulkUpsert(items)
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(e)
              console.log("couldn't fetch recent items")
            }
          }
        }

        void call()
      },
      [
        rxCollection,
        pageLifecycle,
        getItems,
      ],
    )

    return (
      <AggregateLoadingContext.Provider value={state}>
        <AggregateReloadingContextProvider>
          {props.children}
          {followingFollows.map((follow) => (
            <Provider
              key={follow.id}
              followId={follow.id}
              accountId={follow.following_account_id}
              getLoadedRangesForAccountId={getLoadedRangesForAccountId}
              setLoadedRangesForAccountId={setLoadedRangesForAccountId}
              useReloadingContextHook={useAggregatReloadingContext}
            >
              <AggregateLoadingContextUpdater
                accountId={follow.following_account_id}
                useContextHook={useContextHook}
                setState={setFollowIdState}
              />
            </Provider>
          ))}
        </AggregateReloadingContextProvider>
      </AggregateLoadingContext.Provider>
    )
  }

  return [
    AggregateLoadingContextProvider,
    useAggregateLoadingContext,
    useAggregatReloadingContext,
  ]
}
